import { BigNumber } from "bignumber.js";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";
import { useCallback, useContext, useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router";
import PrimeDialog from "../../../components/dialog/PrimeDialog";
import PanelDataTable from "../../../components/PanelDataTable";
import PrimePanel from "../../../components/PrimePanel";
import { useEditableFlag } from "../../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../../store/messageStore";
import NumberUtil from "../../../utility/NumberUtil";
import { F106030801Before, queryCbamEnerguUsage, updateCbamEnerguUsage } from "../../../utility/service/cbam-service";
import { getCBAMSubItemsByItemNo } from "../../../utility/service/companyOffering-service";
import { Base64Encoder, BcryptEncoder, checkDpSuccess } from "../../../utility/service/tool-service";
import { addFormValidator } from "../../../utility/validate/formValidator";
import { get } from "sortablejs";

const EnergyUsageInfo = ({ setActiveIndex }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, loading] = useContext(SystemLoading);
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const [dataQualityList, setDataQualityList] = useState([]);
  const [justificationList, setJustificationList] = useState([]);
  const [quarityAssuranceList, setQuarityAssuranceList] = useState([]);
  const [cbamEmissionsEnergySeq, setCbamEmissionsEnergySeq] = useState("");
  const isEditable = useEditableFlag("F10603");
  const [tabAction, setTabAction] = useState("detail");
  const editFlag = tabAction === "detail" || !isEditable;
  //表單驗證
  const [formRule, setFormRule] = useState([]);
  const {
    action,
    setAction,
    itemObj,
    setItemObj,
    currentCompany,
    setCurrentCompany,
    currentCbamMasterSeq,
    setCurrentCbamMasterSeq,
  } = useOutletContext();
  const [oriData, setOriData] = useState({});
  const [calculationAuditFlag, setCalculationAuditFlag] = useState(false);
  const [auditLogList, setAuditLogList] = useState([]);
  const [logColumns, setLogColumns] = useState([]);

  const {
    getValues,
    setValue,
    watch,
    control,
    register,
    handleSubmit,
    clearErrors,
    trigger,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {
      tmpList: [],
    },
    //錯誤驗證時機
    mode: "all",
  });

  const selectedDataQualityInfo = watch("dataQualityInfo");

  useEffect(() => {
    axios_F106030801Before();
    axios_queryCbamEnerguUsage({ companySeq: currentCompany?.companySeq, cbamMasterSeq: currentCbamMasterSeq });
    axios_getCBAMSubItemsByItemNo("DATA_QUALITY", 9).then(setDataQualityList);
    axios_getCBAMSubItemsByItemNo("JUSTIFICATION", 10).then(setJustificationList);
    axios_getCBAMSubItemsByItemNo("QUALITY_ASSURANCE", 11).then(setQuarityAssuranceList);
  }, []);

  const axios_getCBAMSubItemsByItemNo = (itemNo, orderNo) => {
    return getCBAMSubItemsByItemNo(itemNo, orderNo).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        return data.map((item, index) => ({
          ...item,
          index,
        }));
      }
      return [];
    });
  };

  const axios_queryCbamEnerguUsage = (reqBody) => {
    queryCbamEnerguUsage(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          let data = res.data.RespBody;
          setCbamEmissionsEnergySeq(data?.cbamEmissionsEnergySeq);

          const totalDirectEmissions = new BigNumber(data?.totalCo2EmissionsManual ?? 0)
            .plus(new BigNumber(data?.totalN2oEmissionsManual ?? 0))
            .plus(new BigNumber(data?.totalPfcEmissionsManual ?? 0));

          const oriData = {
            fuelBalanceList: [
              {
                fuelBalance: `B${t("emission_source")}`,
                unit: "TJ",
                totalFuelInputFromB: data?.totalFuelInputFromB,
                disabledColumns: [
                  "unit",
                  "totalFuelInputFromB",
                  "fuelCbamGoodsManual",
                  "fuelElectricityManual",
                  "fuelNonCbamManual",
                ],
              },
              {
                fuelBalance: t("manualInput"),
                unit: "TJ",
                totalFuelInputFromB: data?.totalFuelInputManual,
                fuelCbamGoodsManual: data?.fuelCbamGoodsManual,
                fuelElectricityManual: data?.fuelElectricityManual,
                fuelNonCbamManual: data?.fuelNonCbamManual,
              },
              {
                fuelBalance: resultTip(),
                unit: "TJ",
                totalFuelInputFromB: new BigNumber(data?.totalFuelInputManual ?? data?.totalFuelInputFromB),
                fuelCbamGoodsManual: new BigNumber(data?.fuelCbamGoodsManual),
                fuelElectricityManual: new BigNumber(data?.fuelElectricityManual),
                fuelNonCbamManual: new BigNumber(data?.fuelNonCbamManual),
                disabledColumns: [
                  "unit",
                  "totalFuelInputFromB",
                  "fuelCbamGoodsManual",
                  "fuelElectricityManual",
                  "fuelNonCbamManual",
                ],
              },
            ],
            greenhouseList: [
              {
                fuelBalance: `B${t("emission_source")}`,
                unit: "tCO2e",
                totalCo2EmissionsManual: data?.totalCo2EmissionsFromB,
                biomassEmissions: data?.biomassEmissionsFromB,
                totalN2oEmissions: data?.totalN2oEmissionsFromB,
                totalPfcEmissions: data?.totalPfcEmissionsFromB,
                totalDirectEmissions: data?.totalDirectEmissionsFromB,
                totalEmissions: "",
                disabledColumns: [
                  "unit",
                  "totalCo2EmissionsManual",
                  "biomassEmissions",
                  "totalN2oEmissions",
                  "totalPfcEmissions",
                  "totalDirectEmissions",
                  "totalIndirectEmissionsManual",
                ],
              },
              {
                fuelBalance: t("manualInput"),
                unit: "tCO2e",
                totalCo2EmissionsManual: data?.totalCo2EmissionsManual,
                biomassEmissions: data?.biomassEmissionsManual,
                totalN2oEmissions: data?.totalN2oEmissionsManual,
                totalPfcEmissions: data?.totalPfcEmissionsManual,
                totalIndirectEmissionsManual: data?.totalIndirectEmissionsManual,
                totalEmissions: "",
                disabledColumns: ["totalDirectEmissions"],
              },
              {
                fuelBalance: resultTip(),
                unit: "TJ",
                totalCo2EmissionsManual: new BigNumber(data?.totalCo2EmissionsManual ?? data?.totalCo2EmissionsFromB),
                biomassEmissions: new BigNumber(data?.biomassEmissionsManual ?? data?.biomassEmissionsFromB),
                totalN2oEmissions: new BigNumber(data?.totalN2oEmissionsManual ?? data?.totalN2oEmissionsFromB),
                totalPfcEmissions: new BigNumber(data?.totalPfcEmissionsManual ?? data?.totalPfcEmissionsFromB),
                totalDirectEmissions:
                  data?.totalCo2EmissionsManual || data?.totalN2oEmissionsManual || data?.totalPfcEmissionsManual
                    ? totalDirectEmissions
                    : new BigNumber(data?.totalDirectEmissionsFromB),
                totalIndirectEmissionsManual: new BigNumber(data?.totalIndirectEmissionsManual),
                totalEmissions: "",
                disabledColumns: [
                  "unit",
                  "totalCo2EmissionsManual",
                  "biomassEmissions",
                  "totalN2oEmissions",
                  "totalPfcEmissions",
                  "totalDirectEmissions",
                  "totalIndirectEmissionsManual",
                ],
              },
            ],
            dataQualityInfo: data?.dataQualityInfo,
            defaultValueJustification: data?.defaultValueJustification,
            qualityAssuranceInfo: data?.qualityAssuranceInfo,
            totalCo2EmissionsFromBDetail: data?.totalCo2EmissionsFromBDetail,
            totalFuelInputFromBDetail: data?.totalFuelInputFromBDetail,
          };
          setOriData(oriData);
          Object.entries(oriData).forEach(([key, value]) => {
            setValue(key, value);
          });
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  const axios_F106030801Before = () => {
    checkLoading(loading);
    F106030801Before()
      .then((res) => {
        if (checkDpSuccess(res)) {
          setFormRule(processConstraints(res?.data?.RespBody?.constraints));
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  const processConstraints = (constraints) => {
    return constraints.map((item) => {
      if (item.type === "collection") {
        return { [item?.field]: processConstraints(item?.subObject.value) };
      } else {
        return addFormValidator(item?.field, constraints);
      }
    });
  };

  const setValidForm = useCallback(
    (formId) => {
      if (formRule?.length > 0) {
        const tmp = formRule?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [formRule],
  );

  const { fields: fuelBalanceFields, update: updateFuelBalance } = useFieldArray({
    control,
    name: "fuelBalanceList",
  });

  const fuelBalancePanelConfig = {
    header: () => (
      <>
        <span>{t("fuelBalanceEnergyConsumption")} </span>
        <Button
          type="button"
          icon="pi pi-history"
          className="p-button-icon-only"
          tooltip={t("calculationProcess")}
          tooltipOptions={{ position: "top" }}
          rounded
          text
          severity="secondary"
          size="small"
          onClick={(e) => {
            e.preventDefault();
            setCalculationAuditFlag(true);
            setAuditLogList(oriData.totalFuelInputFromBDetail);
            setLogColumns([
              { value: "sourceName", label: t("rawMaterial") },
              {
                type: "number",
                value: "fossil",
                label: `CO2e${t("log.fossil")}(t)`,
              },
              {
                type: "number",
                value: "bio",
                label: `CO2e${t("log.bio")}(t)`,
              },
              {
                type: "number",
                value: "total",
                label: t("operator.sum"),
              },
            ]);
          }}
        />
      </>
    ),
  };

  const fuelBalanceDataTableConfig = {
    dataKey: "id",
    value: fuelBalanceFields ?? [],
    className: "last-row-no-border",
    item: {
      display: false,
    },
  };

  const fuelBalanceColumnsConfig = [
    // 燃料平衡
    {
      field: "fuelBalance",
      fieldName: "fuelBalance",
      fieldListName: "fuelBalanceList",
      header: t("fuelBalance"),
      frozen: true,
      alignFrozen: "left",
      nonEditor: (value) => true,
    },
    // 單位
    {
      field: "unit",
      fieldName: "unit",
      fieldListName: "fuelBalanceList",
      header: t("unit"),
      nonEditor: (value) => true,
    },
    // 總燃料投入
    {
      field: "totalFuelInputFromB",
      fieldName: "totalFuelInputFromB",
      fieldListName: "fuelBalanceList",
      header: t("totalFuelInput"),
      type: "number",
      control: control,
      rules: setValidForm("totalFuelInputFromB"),
      value: (rowIndex) => watch(`fuelBalanceList.${rowIndex}.totalFuelInputFromB`),
      onChange: (rowIndex, newValue) => {
        if (newValue) {
          setValue(`fuelBalanceList.${rowIndex}.totalFuelInputFromB`, newValue);
          const oldValue = fuelBalanceFields[2];
          updateFuelBalance(2, { ...oldValue, totalFuelInputFromB: newValue });
        } else {
          const oldValue = fuelBalanceFields[2];
          updateFuelBalance(2, {
            ...oldValue,
            totalFuelInputFromB: getValues(`fuelBalanceList.0.totalFuelInputFromB`),
          });
        }
      },
      disabled: editFlag,
    },
    // CBAM產品直接燃料
    {
      field: "fuelCbamGoodsManual",
      fieldName: "fuelCbamGoodsManual",
      fieldListName: "fuelBalanceList",
      header: t("cbamProductDirectFuel"),
      type: "number",
      control: control,
      rules: setValidForm("fuelCbamGoodsManual"),
      value: (rowIndex) => watch(`fuelBalanceList.${rowIndex}.fuelCbamGoodsManual`),
      onChange: (rowIndex, newValue) => {
        if (newValue) {
          setValue(`fuelBalanceList.${rowIndex}.fuelCbamGoodsManual`, newValue);
          const oldValue = fuelBalanceFields[2];
          updateFuelBalance(2, { ...oldValue, fuelCbamGoodsManual: newValue });
        } else {
          const oldValue = fuelBalanceFields[2];
          updateFuelBalance(2, {
            ...oldValue,
            fuelCbamGoodsManual: getValues(`fuelBalanceList.0.fuelCbamGoodsManual`),
          });
        }
      },
      disabled: editFlag,
    },
    // 發電用燃料
    {
      field: "fuelElectricityManual",
      fieldName: "fuelElectricityManual",
      fieldListName: "fuelBalanceList",
      header: t("fuelForPowerGeneration"),
      type: "number",
      control: control,
      rules: setValidForm("fuelElectricityManual"),
      value: (rowIndex) => watch(`fuelBalanceList.${rowIndex}.fuelElectricityManual`),
      onChange: (rowIndex, newValue) => {
        if (newValue) {
          setValue(`fuelBalanceList.${rowIndex}.fuelElectricityManual`, newValue);
          const oldValue = fuelBalanceFields[2];
          updateFuelBalance(2, { ...oldValue, fuelElectricityManual: newValue });
        } else {
          const oldValue = fuelBalanceFields[2];
          updateFuelBalance(2, {
            ...oldValue,
            fuelElectricityManual: getValues(`fuelBalanceList.0.fuelElectricityManual`),
          });
        }
      },
      disabled: editFlag,
    },
    // 非CBAM產品直接燃料
    {
      field: "fuelNonCbamManual",
      fieldName: "fuelNonCbamManual",
      fieldListName: "fuelBalanceList",
      header: t("nonCbamProductDirectFuel"),
      type: "number",
      control: control,
      rules: setValidForm("fuelNonCbamManual"),
      value: (rowIndex) => watch(`fuelBalanceList.${rowIndex}.fuelNonCbamManual`),
      onChange: (rowIndex, newValue) => {
        if (newValue) {
          setValue(`fuelBalanceList.${rowIndex}.fuelNonCbamManual`, newValue);
          const oldValue = fuelBalanceFields[2];
          updateFuelBalance(2, { ...oldValue, fuelNonCbamManual: newValue });
        } else {
          const oldValue = fuelBalanceFields[2];
          updateFuelBalance(2, {
            ...oldValue,
            fuelNonCbamManual: getValues(`fuelBalanceList.0.fuelNonCbamManual`),
          });
        }
      },
      disabled: editFlag,
    },
    // 其他
    {
      field: "other",
      fieldName: "other",
      fieldListName: "fuelBalanceList",
      header: t("other"),
      type: "number",
      nonEditor: (value) => true,
      displayText: (rowData, rowIndex) => {
        const totalFuelInputFromB = new BigNumber(watch(`fuelBalanceList.${rowIndex}.totalFuelInputFromB`) || 0);
        const fuelCbamGoodsManual = new BigNumber(watch(`fuelBalanceList.${rowIndex}.fuelCbamGoodsManual`) || 0);
        const fuelElectricityManual = new BigNumber(watch(`fuelBalanceList.${rowIndex}.fuelElectricityManual`) || 0);
        const fuelNonCbamManual = new BigNumber(watch(`fuelBalanceList.${rowIndex}.fuelNonCbamManual`) || 0);
        const value = totalFuelInputFromB.minus(
          fuelCbamGoodsManual.plus(fuelElectricityManual).plus(fuelNonCbamManual),
        );
        return rowIndex === 2 ? value : "";
      },
    },
  ];

  const { fields: greenhouseFields, update: updateGreenhouse } = useFieldArray({
    control,
    name: "greenhouseList",
  });

  const greenhousePanelConfig = {
    header: () => (
      <>
        <span>{t("ghgEmissionsBalanceAndDataQuality")} </span>
        <Button
          type="button"
          icon="pi pi-history"
          className="p-button-icon-only"
          tooltip={t("calculationProcess")}
          tooltipOptions={{ position: "top" }}
          rounded
          text
          severity="secondary"
          size="small"
          onClick={(e) => {
            e.preventDefault();
            setCalculationAuditFlag(true);
            setAuditLogList(oriData.totalCo2EmissionsFromBDetail);
            setLogColumns([
              { value: "type", label: "", body: (rowData) => t(`log.${rowData?.type}`) },
              { value: "subType", label: "", body: (rowData) => t(`log.${rowData?.subType}`) },
              { value: "sourceName", label: t("rawMaterial") },
              {
                type: "number",
                value: "combustion",
                label: t("log.combustion"),
              },
              {
                type: "number",
                value: "process",
                label: t("log.process"),
              },
              {
                type: "number",
                value: "massBalance",
                label: t("log.massBalance"),
              },
              {
                type: "number",
                value: "measurementProcessing",
                label: t("log.measurementProcessing"),
              },
            ]);
          }}
        />
      </>
    ),
  };

  const greenhouseDataTableConfig = {
    dataKey: "id",
    value: greenhouseFields ?? [],
    className: "last-row-no-border",
    item: {
      display: false,
    },
  };

  const greenhouseColumnsConfig = [
    // 燃料平衡
    {
      field: "fuelBalance",
      fieldName: "fuelBalance",
      fieldListName: "greenhouseList",
      header: t("fuelBalance"),
      frozen: true,
      alignFrozen: "left",
      nonEditor: (value) => true,
    },
    // 單位
    {
      field: "unit",
      fieldName: "unit",
      fieldListName: "greenhouseList",
      header: t("unit"),
      nonEditor: (value) => true,
    },
    // CO2總排放量
    {
      field: "totalCo2EmissionsManual",
      fieldName: "totalCo2EmissionsManual",
      fieldListName: "greenhouseList",
      header: t("co2TotalEmissions"),
      type: "number",
      control: control,
      rules: setValidForm("fuelCbamGoodsManual"),
      value: (rowIndex) => watch(`greenhouseList.${rowIndex}.totalCo2EmissionsManual`),
      onChange: (rowIndex, newValue) => {
        if (newValue) {
          setValue(`greenhouseList.${rowIndex}.totalCo2EmissionsManual`, newValue);
          const oldValue = greenhouseFields[2];
          const newValueBN = new BigNumber(newValue ?? 0);
          const n2oBN = new BigNumber(watch("greenhouseList.2.totalN2oEmissions") ?? 0);
          const pfcBN = new BigNumber(watch("greenhouseList.2.totalPfcEmissions") ?? 0);
          const totalDirectEmissionsValue = newValueBN.plus(n2oBN).plus(pfcBN);
          updateGreenhouse(2, {
            ...oldValue,
            totalCo2EmissionsManual: newValue,
            totalDirectEmissions: String(totalDirectEmissionsValue),
          });
        } else {
          const oldValue = greenhouseFields[2];
          updateGreenhouse(2, {
            ...oldValue,
            totalCo2EmissionsManual: getValues(`greenhouseList.0.totalCo2EmissionsManual`),
          });
        }
      },
      disabled: editFlag,
    },
    // 生物質排放量
    {
      field: "biomassEmissions",
      fieldName: "biomassEmissions",
      fieldListName: "greenhouseList",
      header: t("biomassEmissions"),
      type: "number",
      control: control,
      rules: setValidForm("biomassEmissions"),
      value: (rowIndex) => watch(`greenhouseList.${rowIndex}.biomassEmissions`),
      onChange: (rowIndex, newValue) => {
        if (newValue) {
          setValue(`greenhouseList.${rowIndex}.biomassEmissions`, newValue);
          const oldValue = greenhouseFields[2];
          updateGreenhouse(2, { ...oldValue, biomassEmissions: newValue });
        } else {
          const oldValue = greenhouseFields[2];
          updateGreenhouse(2, {
            ...oldValue,
            biomassEmissions: getValues(`greenhouseList.0.biomassEmissions`),
          });
        }
      },
      disabled: editFlag,
    },
    // N2O排放量
    {
      field: "totalN2oEmissions",
      fieldName: "totalN2oEmissions",
      fieldListName: "greenhouseList",
      header: t("n2oEmissions"),
      type: "number",
      control: control,
      rules: setValidForm("totalN2oEmissions"),
      value: (rowIndex) => watch(`greenhouseList.${rowIndex}.totalN2oEmissions`),
      onChange: (rowIndex, newValue) => {
        if (newValue) {
          setValue(`greenhouseList.${rowIndex}.totalN2oEmissions`, newValue);
          const oldValue = greenhouseFields[2];
          const newValueBN = new BigNumber(newValue ?? 0);
          const co2BN = new BigNumber(watch("greenhouseList.2.totalCo2EmissionsManual") ?? 0);
          const pfcBN = new BigNumber(watch("greenhouseList.2.totalPfcEmissions") ?? 0);
          const totalDirectEmissionsValue = newValueBN.plus(co2BN).plus(pfcBN);
          updateGreenhouse(2, {
            ...oldValue,
            totalN2oEmissions: newValue,
            totalDirectEmissions: String(totalDirectEmissionsValue),
          });
        } else {
          const oldValue = greenhouseFields[2];
          updateGreenhouse(2, {
            ...oldValue,
            totalN2oEmissions: getValues(`greenhouseList.0.totalN2oEmissions`),
          });
        }
      },
      disabled: editFlag,
    },
    // PFC排放量
    {
      field: "totalPfcEmissions",
      fieldName: "totalPfcEmissions",
      fieldListName: "greenhouseList",
      header: t("pfcEmissions"),
      type: "number",
      control: control,
      rules: setValidForm("totalPfcEmissions"),
      value: (rowIndex) => watch(`greenhouseList.${rowIndex}.totalPfcEmissions`),
      onChange: (rowIndex, newValue) => {
        if (newValue) {
          setValue(`greenhouseList.${rowIndex}.totalPfcEmissions`, newValue);
          const oldValue = greenhouseFields[2];
          const newValueBN = new BigNumber(newValue ?? 0);
          const co2BN = new BigNumber(watch("greenhouseList.2.totalCo2EmissionsManual") ?? 0);
          const n2oBN = new BigNumber(watch("greenhouseList.2.totalN2oEmissions") ?? 0);
          const totalDirectEmissionsValue = newValueBN.plus(co2BN).plus(n2oBN);
          updateGreenhouse(2, {
            ...oldValue,
            totalPfcEmissions: newValue,
            totalDirectEmissions: String(totalDirectEmissionsValue),
          });
        } else {
          const oldValue = greenhouseFields[2];
          updateGreenhouse(2, {
            ...oldValue,
            totalPfcEmissions: getValues(`greenhouseList.0.totalPfcEmissions`),
          });
        }
      },
      disabled: editFlag,
    },
    // 直接總排放量
    {
      field: "totalDirectEmissions",
      fieldName: "totalDirectEmissions",
      fieldListName: "greenhouseList",
      header: t("directTotalEmissions"),
      type: "number",
      control: control,
      rules: setValidForm("totalDirectEmissions"),
      onChange: (rowIndex, newValue) => {
        setValue(`greenhouseList.${rowIndex}.totalDirectEmissions`, newValue);
        const oldValue = greenhouseFields[2];
        updateGreenhouse(2, { ...oldValue, totalDirectEmissions: newValue });
      },
      disabled: editFlag,
      value: (rowIndex) => watch(`greenhouseList.${rowIndex}.totalDirectEmissions`),
    },
    // 間接總排放量
    {
      field: "totalIndirectEmissionsManual",
      fieldName: "totalIndirectEmissionsManual",
      fieldListName: "greenhouseList",
      header: t("indirectTotalEmissions"),
      type: "number",
      control: control,
      rules: setValidForm("totalIndirectEmissionsManual"),
      onChange: (rowIndex, newValue) => {
        setValue(`greenhouseList.${rowIndex}.totalIndirectEmissionsManual`, newValue);
        const oldValue = greenhouseFields[2];
        updateGreenhouse(2, { ...oldValue, totalIndirectEmissionsManual: newValue });
      },
      disabled: editFlag,
      value: (rowIndex) => watch(`greenhouseList.${rowIndex}.totalIndirectEmissionsManual`),
    },
    // 總排放量
    {
      field: "totalEmissions",
      fieldName: "totalEmissions",
      fieldListName: "greenhouseList",
      header: t("totalEmissions"),
      type: "number",
      nonEditor: (value) => true,
      displayText: (rowData, rowIndex) => {
        const totalDirectEmissions = new BigNumber(watch(`greenhouseList.${rowIndex}.totalDirectEmissions`) || 0);
        const totalIndirectEmissionsManual = new BigNumber(
          watch(`greenhouseList.${rowIndex}.totalIndirectEmissionsManual`) || 0,
        );
        const value = totalDirectEmissions.plus(totalIndirectEmissionsManual);
        return rowIndex === 2 ? value : "";
      },
    },
  ];

  const axios_updateCbamEnerguUsage = (reqBody) => {
    updateCbamEnerguUsage(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
          setActiveIndex(4);
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  const findItemIndex = (array, code) => {
    let result = array?.filter((item) => item.subitemNo === code);
    return result.length > 0 ? result[0].index : false;
  };

  const encoder = (dataList, code) => {
    if (code === null || code === undefined || code === "") return "";
    if (dataList === null || dataList === undefined || dataList === "" || dataList.length <= 0) return "";
    return Base64Encoder(BcryptEncoder(code ?? "")) + "," + findItemIndex(dataList, code ?? "");
  };

  const resultTip = () => {
    return (
      <>
        <Tooltip key="tooltip" target=".form-exclamation" position="top" />
        <span key="spacer" />
        <span
          key="icon"
          className="form-exclamation"
          data-pr-tooltip={t("cbamEnergyResultMsg")}
          style={{ "--bg-color": "var(--primary-color)" }}
        >
          {t("result")}
        </span>
      </>
    );
  };

  const onSubmit = (data) => {
    let reqBody = {
      cbamEmissionsEnergySeq: cbamEmissionsEnergySeq,
      totalFuelInputManual: data.fuelBalanceList[1].totalFuelInputFromB,
      fuelCbamGoodsManual: data.fuelBalanceList[1].fuelCbamGoodsManual,
      fuelElectricityManual: data.fuelBalanceList[1].fuelElectricityManual,
      fuelNonCbamManual: data.fuelBalanceList[1].fuelNonCbamManual,
      totalCo2EmissionsManual: data.greenhouseList[1].totalCo2EmissionsManual,
      biomassEmissionsManual: data.greenhouseList[1].biomassEmissions,
      totalN2oEmissionsManual: data.greenhouseList[1].totalN2oEmissions,
      totalPfcEmissionsManual: data.greenhouseList[1].totalPfcEmissions,
      totalIndirectEmissionsManual: data.greenhouseList[1].totalIndirectEmissionsManual,
      totalDirectEmissions: data.greenhouseList[1].totalDirectEmissions,
      dataQualityInfo: encoder(dataQualityList, data.dataQualityInfo),
      defaultValueJustification: encoder(justificationList, data.defaultValueJustification),
      qualityAssuranceInfo: encoder(quarityAssuranceList, data.qualityAssuranceInfo),
    };
    axios_updateCbamEnerguUsage(reqBody);
  };

  return (
    <form className="grid" onSubmit={handleSubmit(onSubmit)}>
      {/* 燃料平衡：各種用途的能源消耗量 */}
      <div className="col-12">
        <PanelDataTable
          panelConfig={fuelBalancePanelConfig}
          dataTableConfig={fuelBalanceDataTableConfig}
          columnsConfig={fuelBalanceColumnsConfig}
        />
      </div>
      {/* 溫室氣體排放平衡與數據質量信息  */}
      <div className="col-12">
        <PanelDataTable
          panelConfig={greenhousePanelConfig}
          dataTableConfig={greenhouseDataTableConfig}
          columnsConfig={greenhouseColumnsConfig}
        />
      </div>
      {/* 數據質量和質量保證信息 */}
      <div className="col-12">
        <PrimePanel toggleable className="evaluation-temp-setting-panel" header={t("dataQualityAndAssurance")}>
          <div className="grid">
            {/* 數據收集方式 */}
            <div className="col-4">
              <label htmlFor="dataQualityInfo" className="form-label">
                {t("dataCollectionMethod")}
              </label>
              <Controller
                name={`dataQualityInfo`}
                control={control}
                rules={setValidForm("dataQualityInfo")}
                render={({ field, fieldState }) => (
                  <Dropdown
                    disabled={editFlag}
                    filter
                    showClear
                    appendTo={"self"}
                    optionLabel="subitemName"
                    optionValue="subitemNo"
                    value={field.value}
                    virtualScrollerOptions={{ itemSize: 40 }}
                    defaultValue={""}
                    placeholder={t("plz_chs")}
                    name="dataQualityInfo"
                    control={control}
                    options={dataQualityList}
                    className={"w-full"}
                    onChange={(event) => {
                      field.onChange(event.value);
                      setValue("dataQualityInfo", event.value);
                    }}
                  ></Dropdown>
                )}
              ></Controller>
            </div>
            {/* 使用預設值之理由 */}
            {selectedDataQualityInfo && (
              <div className="col-4">
                <label htmlFor="defaultValueJustification" className="form-label">
                  {t("defaultValueJustification")}
                </label>
                <Controller
                  name={`defaultValueJustification`}
                  control={control}
                  rules={setValidForm("defaultValueJustification")}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      disabled={editFlag}
                      filter
                      showClear
                      appendTo={"self"}
                      optionLabel="subitemName"
                      optionValue="subitemNo"
                      value={field.value}
                      virtualScrollerOptions={{ itemSize: 40 }}
                      defaultValue={""}
                      placeholder={t("plz_chs")}
                      name="defaultValueJustification"
                      control={control}
                      options={justificationList}
                      className={"w-full"}
                      onChange={(event) => {
                        field.onChange(event.value);
                      }}
                    ></Dropdown>
                  )}
                ></Controller>
              </div>
            )}
            {/* 品質保證方式 */}
            <div className="col-4">
              <label htmlFor="qualityAssuranceInfo" className="form-label">
                {t("qualityAssuranceMethod")}
              </label>
              <Controller
                name={`qualityAssuranceInfo`}
                control={control}
                rules={setValidForm("qualityAssuranceInfo")}
                render={({ field, fieldState }) => (
                  <Dropdown
                    disabled={editFlag}
                    filter
                    showClear
                    appendTo={"self"}
                    optionLabel="subitemName"
                    optionValue="subitemNo"
                    value={field.value}
                    virtualScrollerOptions={{ itemSize: 40 }}
                    defaultValue={""}
                    placeholder={t("plz_chs")}
                    name="qualityAssuranceInfo"
                    control={control}
                    options={quarityAssuranceList}
                    className={"w-full"}
                    onChange={(event) => {
                      field.onChange(event.value);
                    }}
                  ></Dropdown>
                )}
              ></Controller>
            </div>
          </div>
        </PrimePanel>
      </div>

      <div className="col-12 gap-2 flex-all-center">
        {tabAction === "detail" && (
          <Button
            disabled={!isEditable}
            type="button"
            label={t("button.edit")}
            size="small"
            onClick={() => setTabAction("create")}
          />
        )}
        {tabAction === "create" && (
          <>
            <Button
              disabled={!(isValid && isDirty)}
              type="submit"
              label={action === "create" ? t("button.create") : t("button.update")}
              size="small"
            />
            {action !== "create" && (
              <Button
                outlined
                size="small"
                label={t("button.cancel")}
                onClick={(e) => {
                  e.preventDefault();
                  clearErrors();
                  setTabAction("detail");
                  reset(oriData);
                }}
              />
            )}
          </>
        )}
      </div>

      {/* 計算過程 */}
      <PrimeDialog
        visible={calculationAuditFlag}
        onClose={() => {
          setCalculationAuditFlag(false);
        }}
        draggable={true}
        resizable={true}
        isShowConfBtn={false}
        isShowDelBtn={false}
        title={`${t("calculationProcess")}`}
        bodyTemplate={
          <DataTable
            className="white-data-table hover-highlight no-shadow"
            value={auditLogList}
            emptyMessage={t("no_rec")}
            rowGroupMode="rowspan"
            groupRowsBy={["type", "subType"]}
          >
            {logColumns?.map((item, index) => (
              <Column
                key={item.value}
                field={item.value}
                header={item.label}
                style={{ textAlign: item.type === "number" ? "right" : "left" }}
                body={(rowData) => {
                  if (item.body) {
                    return item.body(rowData);
                  }
                  if (item.type === "number") {
                    return NumberUtil.formatWithCommas(rowData[item.value]);
                  }
                  return rowData[item.value];
                }}
              />
            ))}
          </DataTable>
        }
      />
    </form>
  );
};

export default EnergyUsageInfo;
